<template>
  <div ref="rootRef" class="collection-embed">
    <!-- Video item — the video player brings its own playlist chrome
         (control-bar next/prev, playlist button, drawer, strip) and reports the
         index it moves to back up to this shell. -->
    <VideoEmbed
      v-if="currentType === 'video'"
      ref="videoEmbedRef"
      :embed="embed as Embed<VideoAssetListItem>"
      :current-index="currentIndex"
      :viewer-id="viewerId"
      :auto-play="autoPlay || resumeOnMount"
      :auto-play-next="autoPlayNext"
      :show-ads="showAds"
      :loop="loop"
      :muted="muted"
      :controls="controls"
      :pin-controls="pinControls"
      :show-playlist="showPlaylist"
      :collection-display-mode="collectionDisplayMode"
      :enable-download="enableDownload"
      :show-chapters="showChapters"
      :show-eco-mode="showEcoMode"
      :eco-mode-default="ecoModeDefault"
      :subtitles-auto-start="subtitlesAutoStart"
      :show-info-button="showInfoButton"
      :enable-pip="enablePip"
      :enable-fullscreen="enableFullscreen"
      :show-homepage-info="showHomepageInfo"
      :show-homepage-play-button="showHomepagePlayButton"
      :poster-fit="posterFit"
      :start-at="startAt"
      :theme="theme"
      :show-logo="showLogo"
      :logo-url="logoUrl"
      :logo-position="logoPosition"
      :logo-click-url="logoClickUrl"
      :primary-color="primaryColor"
      :custom-css="customCss"
      :seeking="seeking"
      @item-change="handleItemChange"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @error="$emit('error', $event)"
      @time-update="$emit('timeUpdate', $event)"
    />

    <!-- Any other item type: a provider's video, or — in a `mixed` collection —
         an audio track, a picture or a document. None of them draws the
         collection's chrome, so the shell renders the same playlist chrome the
         video player would have shown, around the entry's own player. -->
    <div
      v-else
      class="video-player-wrapper"
      :class="wrapperClasses"
      :data-vp-theme="effectiveBaseTheme"
      :style="themeStyle"
      @keydown.escape="isPlaylistOpen = false"
    >
      <div class="video-player-main">
        <div class="video-player" :class="{ 'panel-open': isPlaylistOpen }">
          <div class="video-content">
            <ThirdPartyEmbed
              v-if="currentType === 'third_party'"
              :embed="embed as Embed<ThirdPartyAssetListItem>"
              :item-index="currentIndex"
              :viewer-id="viewerId"
              :theme="effectiveBaseTheme"
              :auto-play="autoPlay || resumeOnMount"
              @error="$emit('error', $event)"
            />
            <!-- A mixed collection's other kinds, each in its own player over a one-entry
                 embed of the collection's token (`entryEmbed`): until 2026-10-15 they drew an
                 empty black frame (QA experience B2). Keyed on the entry, so moving between two
                 entries of one kind mounts the second rather than keeping the first's state. -->
            <AudioEmbed
              v-else-if="currentType === 'audio' && entryEmbed"
              :key="`audio-${currentIndex}`"
              :embed="entryEmbed as Embed<AudioAssetListItem>"
              :viewer-id="viewerId"
              :auto-play="autoPlay || resumeOnMount"
              :auto-play-next="false"
              :show-playlist="false"
              :muted="muted"
              :enable-fullscreen="enableFullscreen"
              :theme="effectiveBaseTheme"
              :icon-color="primaryColor"
              :seeking="seeking"
              @play="$emit('play')"
              @pause="$emit('pause')"
              @ended="handleEntryEnded"
              @error="$emit('error', $event)"
              @time-update="$emit('timeUpdate', $event)"
            />
            <!-- No `@ended` on the image and the document: theirs is a moment of their viewing
                 (`../common/stillEnd.ts`), and an image's comes the moment it shows, which would
                 carry the collection straight past it. They stay until the viewer moves on. -->
            <ImageEmbed
              v-else-if="currentType === 'image' && entryEmbed"
              :key="`image-${currentIndex}`"
              :embed="entryEmbed as Embed<ImageAssetListItem>"
              :viewer-id="viewerId"
              :enable-download="enableDownload"
              :enable-fullscreen="enableFullscreen"
              :theme="effectiveBaseTheme"
              :primary-color="primaryColor"
              @error="$emit('error', $event)"
            />
            <PDFEmbed
              v-else-if="currentType === 'document' && entryEmbed"
              :key="`document-${currentIndex}`"
              :embed="entryEmbed as Embed<DocumentAssetListItem>"
              :viewer-id="viewerId"
              :enable-download="enableDownload"
              :theme="effectiveBaseTheme"
              :primary-color="primaryColor"
              @error="$emit('error', $event)"
            />
          </div>

          <!-- Drawer mode: the pill stays visible because a provider iframe
               swallows the mouse events the control bar hides itself on.
               The open drawer traps focus, and the video player answers Escape
               from its keyboard shortcuts; with no player behind a third-party
               item, the shell has to answer it (see the wrapper's handler) or
               the keyboard has no way back out. -->
          <PlaylistIndicator
            v-if="showDrawerChrome"
            :visible="true"
            :current-index="currentIndex"
            :total-items="totalItems"
            @toggle="isPlaylistOpen = !isPlaylistOpen"
          />

          <PlayerPanel
            v-if="showDrawerChrome"
            :open="isPlaylistOpen"
            :title="t('player.controls.playlist')"
            @close="isPlaylistOpen = false"
          >
            <PlaylistPanel :items="items" :current-index="currentIndex" @select="goToIndex" />
          </PlayerPanel>
        </div>

        <VideoThumbnailStrip
          v-if="showStrip && displayMode === 'bottom-bar'"
          :items="items"
          :current-index="currentIndex"
          direction="horizontal"
          @select="goToIndex"
        />
      </div>

      <VideoThumbnailStrip
        v-if="showStrip && displayMode === 'sidebar'"
        :items="items"
        :current-index="currentIndex"
        direction="vertical"
        @select="goToIndex"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, toRef, type PropType } from 'vue'
  import type { Embed, PlayerTheme } from '../types/embed'
  import type {
    AssetListItem,
    AudioAssetListItem,
    DocumentAssetListItem,
    ImageAssetListItem,
    ThirdPartyAssetListItem,
    VideoAssetListItem,
  } from '../types/asset'
  import type { PlayerError } from '../types/base'
  import type { CollectionItem } from '../types/player'
  import type { CollectionDisplayMode } from '../video/types'
  import { usePlayerTheme } from '../common/composables/usePlayerTheme'
  import { useVideoI18n } from '../video/i18n'
  import { useEffectiveDisplayMode } from '../video/composables/useEffectiveDisplayMode'
  import VideoEmbed from '../video/components/VideoEmbed.vue'
  import ThirdPartyEmbed from '../third-party/components/ThirdPartyEmbed.vue'
  import AudioEmbed from '../audio/components/AudioEmbed.vue'
  import ImageEmbed from '../image/components/ImageEmbed.vue'
  import PDFEmbed from '../pdf/components/PDFEmbed.vue'
  import VideoThumbnailStrip from '../video/components/collection/VideoThumbnailStrip.vue'
  import PlaylistPanel from '../video/components/panels/PlaylistPanel.vue'
  import PlayerPanel from '../video/components/panels/PlayerPanel.vue'
  import PlaylistIndicator from '../video/components/overlays/PlaylistIndicator.vue'

  /**
   * Shell for a video collection whose items are not all of the same kind.
   *
   * A collection can hold hosted videos next to videos that live at a provider
   * (YouTube, Vimeo, Dailymotion), and those two need different players. The
   * shell owns the list and the current index, and mounts the player the item
   * at that index requires — so the playlist survives the switch.
   *
   * A collection of hosted videos only takes the `VideoEmbed` branch, which is
   * the exact rendering it had before the shell existed. A `mixed` collection —
   * a video, a document and an audio track side by side — mounts the audio,
   * image and PDF players for its other entries, each over a one-entry embed
   * that keeps the collection's token and sitting.
   */
  const props = defineProps({
    /** Embed data (already loaded by EmbedPlayer) */
    embed: {
      type: Object as PropType<Embed<AssetListItem>>,
      default: undefined,
    },
    /** The entry the shell opens on, 0-based — read when it mounts. */
    startIndex: { type: Number, default: 0 },
    viewerId: { type: String, default: undefined },
    autoPlay: { type: Boolean, default: false },
    autoPlayNext: { type: Boolean, default: true },
    showAds: { type: Boolean, default: true },
    loop: { type: Boolean, default: false },
    muted: { type: Boolean, default: false },
    controls: { type: Boolean, default: true },
    pinControls: { type: Boolean, default: false },
    showPlaylist: { type: Boolean, default: true },
    collectionDisplayMode: {
      type: String as PropType<CollectionDisplayMode>,
      default: 'drawer',
    },
    /** The in-app player's download switch, handed to the video player. */
    enableDownload: { type: Boolean, default: false },
    showChapters: { type: Boolean, default: true },
    showEcoMode: { type: Boolean, default: true },
    ecoModeDefault: { type: Boolean, default: undefined },
    subtitlesAutoStart: { type: Boolean, default: undefined },
    showInfoButton: { type: Boolean, default: true },
    enablePip: { type: Boolean, default: true },
    enableFullscreen: { type: Boolean, default: true },
    showHomepageInfo: { type: Boolean, default: true },
    showHomepagePlayButton: { type: Boolean, default: true },
    posterFit: { type: String as PropType<'contain' | 'cover'>, default: 'contain' },
    startAt: { type: Number, default: undefined },
    theme: { type: String as PropType<'dark' | 'light'>, default: 'dark' },
    showLogo: { type: Boolean, default: true },
    logoUrl: { type: String, default: undefined },
    logoPosition: {
      type: String as PropType<'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'>,
      default: undefined,
    },
    logoClickUrl: { type: String, default: undefined },
    primaryColor: { type: String, default: undefined },
    customCss: { type: String, default: undefined },
    /** Whether the viewer may move the playhead (`seeking`), handed to the entry's player. */
    seeking: { type: Boolean, default: true },
  })

  const emit = defineEmits<{
    itemChange: [index: number, item: CollectionItem<AssetListItem>]
    play: []
    pause: []
    ended: []
    error: [error: PlayerError]
    timeUpdate: [currentTime: number]
  }>()

  const { t } = useVideoI18n()

  const videoEmbedRef = ref<InstanceType<typeof VideoEmbed> | null>(null)

  const items = computed<CollectionItem<AssetListItem>[]>(
    () => (props.embed?.items ?? []) as CollectionItem<AssetListItem>[],
  )
  const totalItems = computed(() => items.value.length)

  // The entry it opens on is the one its first player mounts on, so that entry's playback is the
  // first one fetched; an index past the last entry opens on the first.
  const currentIndex = ref(props.startIndex > 0 && props.startIndex < totalItems.value ? props.startIndex : 0)
  const isPlaylistOpen = ref(false)

  // Switching between two item types unmounts one player and mounts another, so
  // the new one comes back on its poster instead of carrying on. Once the chain
  // is running — the viewer picked an item, or playback advanced on its own —
  // whatever mounts next starts by itself: the pick is the gesture browsers ask
  // for. Providers only honour it muted, so a third-party item resumes silent
  // and the viewer takes the sound back.
  const resumeOnMount = ref(false)

  const currentType = computed(() => items.value[currentIndex.value]?.asset.type)

  /**
   * **The current entry as an embed of its own**, for the players that read a whole embed and
   * have no notion of a mixed list: the collection's token, viewer and sitting
   * (`collectionPlaybackId`) kept, so the entry is opened with `playback/info` under the one token
   * rooted at the collection and recorded inside its sitting, exactly as the video player opens
   * its own entries.
   */
  const entryEmbed = computed<Embed<AssetListItem> | undefined>(() => {
    const embed = props.embed
    const entry = items.value[currentIndex.value]
    if (!embed || !entry) return undefined
    return {
      ...embed,
      embedType: 'single',
      name: entry.asset.name,
      collection: undefined,
      items: [{ ...entry, position: 0 }],
    } as Embed<AssetListItem>
  })

  /** An audio entry played to its end moves the collection on, as the video player's chain does. */
  function handleEntryEnded(): void {
    emit('ended')
    if (props.autoPlayNext && currentIndex.value + 1 < totalItems.value) {
      goToIndex(currentIndex.value + 1)
    }
  }

  // The mode drawn: a narrow shell turns a strip or a sidebar into the drawer, whatever was chosen.
  const rootRef = ref<HTMLElement | null>(null)
  const displayMode = useEffectiveDisplayMode(() => props.collectionDisplayMode, rootRef)

  // Chrome the shell has to provide itself: only the branches that mount a
  // player without one. The video player keeps drawing its own.
  const showStrip = computed(
    () => props.showPlaylist && totalItems.value > 1 && displayMode.value !== 'drawer',
  )
  const showDrawerChrome = computed(
    () => props.showPlaylist && totalItems.value > 1 && displayMode.value === 'drawer',
  )

  const wrapperClasses = computed(() => ({
    'panel-open': isPlaylistOpen.value,
    // The chosen mode and not the drawn one: these classes also size the frame (bottom-bar's is
    // what gives a player with no parent height its 16:9), which must not collapse on a fallback.
    'collection-mode-bottom-bar': props.collectionDisplayMode === 'bottom-bar',
    'collection-mode-sidebar': props.collectionDisplayMode === 'sidebar',
    'collection-mode-drawer': props.collectionDisplayMode === 'drawer',
  }))

  const playerTheme = computed<PlayerTheme | undefined>(() => {
    const base = props.embed?.playerTheme
    const overrides: Partial<PlayerTheme> = { baseTheme: props.theme }
    if (props.primaryColor) overrides.primaryColor = props.primaryColor
    if (props.customCss) overrides.customCss = props.customCss
    return { ...base, ...overrides } as PlayerTheme
  })

  const { themeStyle, effectiveBaseTheme } = usePlayerTheme(playerTheme)

  function goToIndex(index: number): void {
    if (index < 0 || index >= totalItems.value || index === currentIndex.value) return
    currentIndex.value = index
    isPlaylistOpen.value = false
    resumeOnMount.value = true
    emit('itemChange', index, items.value[index])
  }

  /** Index change reported by a player that owns its own playlist chrome. */
  function handleItemChange(index: number, item: CollectionItem<VideoAssetListItem>): void {
    currentIndex.value = index
    // Reaching here means playback moved on its own or the viewer picked from
    // the video player's own drawer — either way the chain is running, so the
    // next item starts by itself even when it mounts a different player.
    resumeOnMount.value = true
    emit('itemChange', index, item as CollectionItem<AssetListItem>)
  }

  // A collection shorter than the current position (items reloaded) must not
  // leave the shell pointing at nothing.
  watch(totalItems, (total) => {
    if (currentIndex.value >= total) currentIndex.value = Math.max(0, total - 1)
  })

  const embedRef = toRef(props, 'embed')
  watch(embedRef, () => {
    currentIndex.value = 0
    resumeOnMount.value = false
  })

  defineExpose({
    currentIndex,
    goToIndex,
    play: () => videoEmbedRef.value?.play(),
    pause: () => videoEmbedRef.value?.pause(),
    seek: (time: number) => videoEmbedRef.value?.seek(time),
    startPlayback: () => videoEmbedRef.value?.startPlayback(),
    duration: computed(() => videoEmbedRef.value?.duration),
  })
</script>

<style lang="scss" scoped>
  .collection-embed {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
</style>
